<template>
	<div class="flex w-full relative items-center">
		<Arrow
			v-if="showArrows"
			arrow-direction="left"
			:number-of-slides-to-show="blok.number_of_slides_to_show"
			:scroll-position="scrollPosition"
			:number-of-slides="slides.length"
			@click="slideLeft"
		/>

		<div
			ref="carouselRef"
			class="flex cursor-pointer overflow-x-auto w-full carousel pb-8"
			@mousedown="handleDragStart"
			@mouseup="handleDragEnd"
			@mouseleave="handleDragEnd"
			@mousemove="handleDrag"
		>
			<div
				v-for="slide in slides"
				:key="slide.Id"
				:class="[
					`flex flex-col last:mr-0 first:ml-0 gap-4 text-center  mx-2 ${
						typeOfSlide === ItemType.CATEGORY ? categoriesSize : wrapperSize
					}`
				]"
			>
				<NuxtLink
					v-if="typeOfSlide === ItemType.CATEGORY"
					:to="
						clickIsValid
							? localePath(
									`/${createCategoryLink({
										category: slide as CategoryWithFiles
									})}`
								)
							: ''
					"
				>
					<CategoryCard
						:category="slide as CategoryWithFiles"
						:lazy-loading="blok.lazy_load_images"
					/>
				</NuxtLink>
				<ProductCard
					v-if="typeOfSlide === ItemType.PRODUCT"
					:product="slide as ProductItem"
					:picture-size="pictureSize"
					:click-is-valid="clickIsValid"
					:lazy-loading="blok.lazy_load_images"
					:show-add-to-cart="showAddToCart"
					@product-click="$emit('product-click', slide)"
				/>
			</div>
		</div>

		<Arrow
			v-if="showArrows"
			arrow-direction="right"
			:number-of-slides-to-show="blok.number_of_slides_to_show"
			:scroll-position="scrollPosition"
			:number-of-slides="slides.length"
			@click="slideRight"
		/>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useResizeObserver } from "@vueuse/core";
import type { CategoryWithFiles } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import type { ProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type { BlokCarousel } from "~/types/storyblok/storyblok";
import { ItemType } from "~/utils/constants";

interface Props {
	blok: BlokCarousel;
	slides: CategoryWithFiles[] | ProductItem[];
	typeOfSlide: string;
	/** On for every product carousel on this site (GF-62); a caller still wins. */
	showAddToCart?: boolean | undefined;
}

const {
	blok,
	slides,
	typeOfSlide,
	showAddToCart = true
} = defineProps<Props>();
defineEmits(["product-click"]);

const localePath = useLocalePath();
const { createCategoryLink } = useCategory();
const carouselRef = ref<HTMLElement>();
const isMouseDown = ref(false);
const clickIsValid = ref(true);
const mouseCoords = ref({
	startX: 0,
	scrollLeft: 0
});

const pictureSizes = {
	six: 199,
	four: 309
};

const showArrows = computed(() => {
	return +blok.number_of_slides_to_show < slides?.length && blok.show_arrows;
});

const scrollPosition = ref(0);
let cancelClick: ReturnType<typeof setTimeout>;
const handleDragStart = (e: MouseEvent) => {
	if (!carouselRef.value) return;
	isMouseDown.value = true;
	cancelClick = setTimeout(() => {
		clickIsValid.value = false;
	}, 200);
	carouselRef.value.classList.remove("scroll-smooth");

	const slider = carouselRef.value;
	const startX = e.pageX - slider.offsetLeft;
	const scrollLeft = slider.scrollLeft;
	mouseCoords.value = { startX, scrollLeft };

	carouselRef.value.style.cursor = "grabbing";

	e.preventDefault();
};
const handleDragEnd = () => {
	clearTimeout(cancelClick);
	clickIsValid.value = true;
	isMouseDown.value = false;
	if (!carouselRef.value) return;
	carouselRef.value.style.cursor = "pointer";
};
const handleDrag = (e: MouseEvent) => {
	if (!isMouseDown.value || !carouselRef.value) return;
	e.preventDefault();

	const x = e.pageX - carouselRef.value.offsetLeft;
	const walkX = (x - mouseCoords.value.startX) * 1.5;
	const scrollLeft = mouseCoords.value.scrollLeft - walkX;
	carouselRef.value.scrollLeft = scrollLeft;
	scrollPosition.value = scrollLeft;
};

const slideRight = () => {
	if (!carouselRef.value) {
		return;
	}
	const stepSize =
		blok.number_of_slides_to_show === "4"
			? pictureSizes.four * 1.065
			: pictureSizes.six * 1.065;
	carouselRef.value.classList.add("scroll-smooth");
	const scrollLeft = carouselRef.value.scrollLeft + stepSize;
	carouselRef.value.scrollLeft = scrollLeft;
	scrollPosition.value = scrollLeft;
};

const slideLeft = () => {
	if (!carouselRef.value) {
		return;
	}
	if (!carouselRef.value.scrollLeft) {
		return;
	}
	const stepSize =
		blok.number_of_slides_to_show === "4"
			? pictureSizes.four * 1.065
			: pictureSizes.six * 1.065;
	carouselRef.value.classList.add("scroll-smooth");
	const scrollLeft = carouselRef.value.scrollLeft - stepSize;
	carouselRef.value.scrollLeft = scrollLeft;
	scrollPosition.value = scrollLeft;
};
const pictureSize = ref<string>("");
const changePictureSize = () => {
	if (blok.number_of_slides_to_show === "6") {
		if (blok.number_of_slides_to_show === "6") {
			if (
				carouselRef?.value &&
				carouselRef?.value?.offsetWidth >
					+blok.number_of_slides_to_show * pictureSizes.six
			) {
				return (pictureSize.value = `w-full h-[${pictureSizes.six}px]`);
			}
			return (pictureSize.value = `w-[${pictureSizes.six}px] h-[${pictureSizes.six}px]`);
		}
	} else if (blok.number_of_slides_to_show === "4") {
		return (pictureSize.value = `w-[${pictureSizes.four}px] h-[${pictureSizes.four}px]`);
	}
};
const categoriesSize = ref<string>("");
const changeCategoriesSize = () => {
	if (blok.number_of_slides_to_show === "6") {
		if (blok.number_of_slides_to_show === "6") {
			if (
				carouselRef?.value &&
				carouselRef?.value?.offsetWidth > +blok.number_of_slides_to_show * 176
			) {
				return (categoriesSize.value = "w-2/12");
			}
			return (categoriesSize.value = "");
		}
	} else if (blok.number_of_slides_to_show === "4") {
		return (categoriesSize.value = "w-4/12 ");
	}
};
const wrapperSize = ref<string>("");
const changeWrapperSize = () => {
	if (blok.number_of_slides_to_show === "6") {
		if (
			carouselRef?.value &&
			carouselRef?.value?.offsetWidth >
				+blok.number_of_slides_to_show * pictureSizes.six
		) {
			return (wrapperSize.value = "w-2/12");
		}
		return (wrapperSize.value = "");
	} else if (blok.number_of_slides_to_show === "4") {
		return `w-[${pictureSizes.four}px] h-[${pictureSizes.four}px]`;
	}
};

onMounted(async () => {
	changeWrapperSize();
	changePictureSize();
	changeCategoriesSize();
});
useResizeObserver(carouselRef, () => {
	changeWrapperSize();
	changePictureSize();
	changeCategoriesSize();
});
</script>
